<template>
	<Col v-if="isLoading" class="height-100 flex-centerize">
		<AppLoader :class="[isShortMode ? 'mt-16 pt-4' : 'mt-n12']" />
	</Col>
	<component v-else :is="activeComponent.component" v-bind="activeComponent.props" v-on="activeComponent.listeners">
		<template #user-select-combobox>
			<ShareDialogUserSelectCombobox
				:workflow="workflow"
				:inviteUserRole.sync="inviteUserRole"
				:isPostPublishMode="isPostPublishMode"
				:minimalBillingLevel="minimalBillingLevel"
				:comboboxSelection.sync="comboboxSelection"
				:usersWithBadBillingLevel="usersWithBadBillingLevel"
				@remove-auto-upgraded-members="removeAutoUpgradedMembers"
			/>
		</template>
	</component>
</template>
<script>
import { FlowType } from '@ply-io/ply-front-shared-utilities';

import { BILLING_LEVEL_TO_RECOMMENDED_ROLE, BILLING_LEVEL_HIERARCHY } from '@/components/Billing/billing-service.const';
import BeforeShareRecommendations from '@/components/dialogs/ShareFeatureDialog/BeforeShareRecommendations.vue';
import ShareDialogUserSelectCombobox from '@/components/dialogs/ShareFeatureDialog/ShareDialogUserSelectCombobox.vue';
import SharedWithTeammates from '@/components/dialogs/ShareFeatureDialog/SharedWithTeammates.vue';
import { getAccountMembers } from '@/store/modules/account/actions';
import { getGroups } from '@/store/modules/accountGroups/actions';
import { getCredentialsSuggestions } from '@/store/modules/credentials/actions';
import { setWorkflowShareData } from '@/store/modules/workflows/actions';
import { mapActionsShorthand } from '@/store/utils/mappers';
import WorkflowService from '@/utils/WorkflowService';
import { AccountRoles } from '@/utils/account-service.const';
export default {
	name: 'ShareDialogContentDataProvider',
	components: {
		SharedWithTeammates,
		BeforeShareRecommendations,
		ShareDialogUserSelectCombobox,
	},
	props: {
		service: { type: Object },
		workflow: { type: Object },
		isShortMode: { type: Boolean },
		isPostPublishMode: { type: Boolean },
	},
	computed: {
		isSelectingUsersToShare() {
			return this.comboboxSelection.length > 0;
		},
		activeComponent() {
			if (this.isSelectingUsersToShare) {
				return this.beforeShareRecommendationComponent;
			} else {
				return this.previouslySharedWithComponent;
			}
		},
		contentProps() {
			return { service: this.service, workflow: this.workflow };
		},
		previouslySharedWithComponent() {
			return {
				component: SharedWithTeammates,
				listeners: { 'show-full-menu': this.onShowFullMenu },
				props: {
					...this.contentProps,
					showLink: this.showLink,
					allowShare: this.allowShare,
					isShortMode: this.isShortMode,
				},
			};
		},
		beforeShareRecommendationComponent() {
			return {
				component: BeforeShareRecommendations,
				listeners: { shared: this.onSharedSuccessfully, 'accepted-suggestions': this.fetchSuggestions },
				props: {
					...this.contentProps,
					inviteUserRole: this.inviteUserRole,
					shareWithSelection: this.comboboxSelection,
					usersWithBadBillingLevel: this.usersWithBadBillingLevel,
					roleToAutoUpgrade: BILLING_LEVEL_TO_RECOMMENDED_ROLE[this.minimalBillingLevel],
				},
			};
		},
		allowShare() {
			return !WorkflowService.isPublicAccess(this.workflow);
		},
		showLink() {
			return this.workflow.flow_type === FlowType.Tool && this.workflow.tool_preferences.url_enabled;
		},
		usersWithBadBillingLevel() {
			return this.comboboxSelection.filter((selection) => this.isBadBillingLevelSelection(selection));
		},
		minimalBillingLevel() {
			return this.service?.billing_level;
		},
	},
	methods: {
		...mapActionsShorthand({ getAccountMembers, getGroups, setWorkflowShareData, getCredentialsSuggestions }),
		onUpdateSelection(selection) {
			this.comboboxSelection = selection;
		},
		removeAutoUpgradedMembers() {
			this.comboboxSelection = this.comboboxSelection.filter((selection) => !this.isBadBillingLevelSelection(selection));
		},
		isMailSelection(selection) {
			return typeof selection === 'string' || selection.suggestion;
		},
		isBadBillingLevelSelection(selection) {
			const minimalBillingLevelStrengthIndex = BILLING_LEVEL_HIERARCHY.indexOf(this.minimalBillingLevel);
			if (this.isMailSelection(selection) || !selection.billing_level) {
				return false;
			}
			return BILLING_LEVEL_HIERARCHY.indexOf(selection.billing_level) < minimalBillingLevelStrengthIndex;
		},
		close() {
			this.$emit('close');
		},
		onShowFullMenu() {
			this.$emit('show-full-menu');
		},
		onSharedSuccessfully() {
			this.comboboxSelection = [];
		},
		async fetchSuggestions() {
			return this.getCredentialsSuggestions({ workflowId: this.workflow.id, fromPublishFlow: false });
		},
		async initializeData() {
			this.isLoading = true;
			try {
				await Promise.all([
					this.getGroups(),
					this.fetchSuggestions(),
					this.getAccountMembers(),
					this.setWorkflowShareData({ id: this.workflow.id, force: true }),
				]);
			} catch (e) {
				this.$toast.error('Something went wrong. Please try again later.');
				this.close();
			} finally {
				this.isLoading = false;
			}
		},
	},
	async mounted() {
		await this.initializeData();
		this.inviteUserRole = BILLING_LEVEL_TO_RECOMMENDED_ROLE[this.service?.billing_level] || AccountRoles.LiteEndUser;
	},
	data() {
		return {
			isLoading: true,
			comboboxSelection: [],
			inviteUserRole: undefined,
		};
	},
	watch: {
		isSelectingUsersToShare: {
			handler(val) {
				val && this.onShowFullMenu();
			},
			immediate: true,
		},
	},
};
</script>
<style scoped lang="scss"></style>
